/**
 * A whole page whose feature runs nothing yet, said plainly (REP-BUILDERS;
 * D661; owner rule "never hide anything": a screen never shows a figure that
 * cannot be right, never an empty column or a silent zero, and never a
 * control that pretends to work).
 *
 * The route stays, so a bookmark or a link still lands, and the page says
 * three things in words: that it is not available yet, what it does not do,
 * and the build-plan slice that brings it (or the register row, when no
 * slice is planned), through the same `pending` reasons a disabled control
 * uses (./PendingAction.tsx), the id isolated left to right inside an Arabic
 * sentence. It renders no control and asks the API for nothing.
 *
 * The navigation labels the same pages: `availability: "notYet"` on their
 * nav-data entries, read by the sidebar and the command palette.
 */
import { useId } from "react";
import { Badge, PageHeader } from "@/components/ui";
import { usePendingReason, type PendingReason } from "./PendingAction";
import { useTranslation } from "@/lib/i18n";

export function NotAvailablePage({
  title,
  message,
  reason,
  slice,
}: {
  /** The page's name, as the navigation shows it. */
  title: string;
  /** What this page does not do yet. */
  message: string;
  /** Why it waits: a `pending` reason. */
  reason: PendingReason;
  /** The build-plan slice, or the register row, it waits for. */
  slice: string;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const why = usePendingReason(reason, slice);
  return (
    <div>
      <PageHeader title={title} />
      <section className="atlas-card" aria-labelledby={headingId}>
        <h2
          id={headingId}
          className="atlas-h2"
          style={{ marginBlock: "0 8px" }}
        >
          {t("notAvailable.title")}
        </h2>
        <p style={{ marginBlock: "0 8px" }}>{message}</p>
        <p style={{ marginBlock: "0 8px" }}>{why}</p>
        <p style={{ margin: 0 }}>{t("notAvailable.noFigures")}</p>
      </section>
    </div>
  );
}

/** The tag beside a navigation entry whose page is not available yet. */
export function NotAvailableTag() {
  const { t } = useTranslation();
  return <Badge className="atlas-nav-tag">{t("notAvailable.navTag")}</Badge>;
}
